import { getLanguage as getInterfaceLanguage, getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useRef } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, DollarSign, Target, ArrowUpDown, ArrowUp, ArrowDown, GitCompare, Download, Layers, Plus, Receipt } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PeriodPicker } from '@/components/ui/period-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { platformsData } from './platformsData';
import { WebhookData, getPlatformLogo, getPlatformName } from './types';
import { KPICard } from './KPICard';
import { PlatformFilter } from './PlatformFilter';
import { Button } from '@/components/ui/button';
import { PlatformComparisonHeader } from './PlatformComparisonHeader';
import { PerformanceHighlights } from './PerformanceHighlights';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import { ConversionModal } from './modals/ConversionModal';
import { ConversionRateModal } from './modals/ConversionRateModal';
import { AverageTicketModal } from './modals/AverageTicketModal';
import { RevenueModal } from './modals/RevenueModal';
import { PlatformDetailsModal } from './modals/PlatformDetailsModal';
import { useTrackingStore } from '@/store/trackingStore';
import { useTrackingData } from './useTrackingData';
import { useTrackingMetricsByDay } from './useTrackingMetricsByDay';
import { platformSlugToApiName } from './platformsData';

interface MetricsTablePremiumProps {
  savedWebhooks?: WebhookData[];
  onBack?: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  dateRange?: DateRange;
}

interface MetricData {
  platform: string;
  video: string;
  conversions: number;
  revenue: number;
  conversionRate: number;
}

type SortField = 'conversions' | 'revenue' | 'conversionRate' | null;
type SortDirection = 'asc' | 'desc';

export function MetricsTablePremium({ savedWebhooks = [], onBack, activeTab, setActiveTab, dateRange }: MetricsTablePremiumProps) {
  useLanguage();
  const { selectedPlatforms } = useTrackingStore();
  const platforms = selectedPlatforms.map(p => p.slug);
  const { data: trackingData } = useTrackingData(dateRange);
  const { data: metricsByDay } = useTrackingMetricsByDay(dateRange);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [compareMode, setCompareMode] = useState<boolean>(false);
  const [selectedComparePlatforms, setSelectedComparePlatforms] = useState<string[]>([]);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const comparisonRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useState<'conversions' | 'rate' | 'ticket' | 'revenue' | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [selectedPlatformForModal, setSelectedPlatformForModal] = useState<{
    platformId: string;
    platformName: string;
    videos: MetricData[];
  } | null>(null);
  
  const exportToPDF = async () => {
    if (!comparisonRef.current) return;
    
    try {
      toast.info(translateInterfaceText("Gerando PDF..."), { duration: 2000 });
      
      const canvas = await html2canvas(comparisonRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`comparacao-plataformas-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success(translateInterfaceText("PDF gerado com sucesso!"), { duration: 3000 });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(translateInterfaceText("Erro ao gerar PDF. Tente novamente."), { duration: 3000 });
    }
  };
  
  // Usar dados reais da API
  const metrics: MetricData[] = trackingData?.integrations?.map(integration => {
    const conversions = Number(integration.total_conversions) || 0;
    const revenue = Number(integration.total_revenue) || 0;
    const conversionRate = Number(integration.conversion_rate) || 0;

    // Criar mapeamento reverso para usar slug ao invés do nome da API
    const apiNameToSlug = Object.fromEntries(
      Object.entries(platformSlugToApiName).map(([slug, apiName]) => [apiName, slug])
    );
    const platformSlug = apiNameToSlug[integration.platform_name] || integration.platform_name;

    return {
      platform: platformSlug,
      video: integration.video_name || 'Sem vídeo',
      conversions,
      revenue,
      conversionRate, // Taxa Média = 100% / número de plataformas
    };
  }) || [];

  // Extrair plataformas ativas únicas dos metrics
  const activePlatforms = Array.from(new Set(metrics.map(m => m.platform)));

  const getPlatformName = (platformId: string) => {
    const platform = platformsData.find(p => p.id === platformId);
    return platform?.name || platformId;
  };
  
  // Calculate metrics for a specific platform
  const getMetricsForPlatform = (platformId: string) => {
    const platformMetrics = metrics.filter(m => m.platform === platformId);
    const conversions = platformMetrics.reduce((sum, m) => sum + m.conversions, 0);
    const revenue = platformMetrics.reduce((sum, m) => sum + m.revenue, 0);
    const conversionRate = platformMetrics.reduce((sum, m) => 
      sum + (m.conversionRate * m.conversions), 0) / (conversions || 1);
    
    return { conversions, revenue, conversionRate, metrics: platformMetrics };
  };
  
  // Filter metrics based on selected platform
  const filteredMetrics = selectedPlatform === 'all' 
    ? metrics 
    : metrics.filter(metric => metric.platform === selectedPlatform);
  
  // Sort metrics based on selected field and direction
  const sortedMetrics = [...filteredMetrics].sort((a, b) => {
    if (!sortField) return 0;
    
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });
  
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with descending as default
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const SortIcon = ({ field }: { field: SortField }) => {
  useLanguage();
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-[#9CA3AF]" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-[#2563EB]" />
      : <ArrowDown className="h-4 w-4 ml-1 text-[#2563EB]" />;
  };
  
  // Calculate summary data based on filtered metrics
  const totalConversions = sortedMetrics.reduce((sum, metric) => sum + metric.conversions, 0);
  const totalRevenue = sortedMetrics.reduce((sum, metric) => sum + metric.revenue, 0);
  
  // Calculate weighted average conversion rate
  const weightedConversionRate = sortedMetrics.reduce((sum, metric) => {
    return sum + (metric.conversionRate * metric.conversions);
  }, 0) / (totalConversions || 1);
  
  // Calculate average ticket
  const averageTicket = totalConversions > 0 ? totalRevenue / totalConversions : 0;
  
  // Calculate best performing platform for each metric
  const platformStats = Array.from(new Set(metrics.map(m => m.platform))).map(platform => {
    const platformMetrics = metrics.filter(m => m.platform === platform);
    const platformConversions = platformMetrics.reduce((sum, m) => sum + m.conversions, 0);
    const platformRevenue = platformMetrics.reduce((sum, m) => sum + m.revenue, 0);
    const platformAvgRate = platformMetrics.reduce((sum, m) => sum + (m.conversionRate * m.conversions), 0) / (platformConversions || 1);
    const platformAvgTicket = platformConversions > 0 ? platformRevenue / platformConversions : 0;
    
    return {
      platform,
      conversions: platformConversions,
      revenue: platformRevenue,
      conversionRate: platformAvgRate,
      avgTicket: platformAvgTicket
    };
  });
  
  const topConversionsPlatform = platformStats.reduce((max, p) => p.conversions > max.conversions ? p : max, platformStats[0] || { platform: '', conversions: 0, revenue: 0, conversionRate: 0, avgTicket: 0 });
  const topRevenuePlatform = platformStats.reduce((max, p) => p.revenue > max.revenue ? p : max, platformStats[0] || { platform: '', conversions: 0, revenue: 0, conversionRate: 0, avgTicket: 0 });
  const topRatePlatform = platformStats.reduce((max, p) => p.conversionRate > max.conversionRate ? p : max, platformStats[0] || { platform: '', conversions: 0, revenue: 0, conversionRate: 0, avgTicket: 0 });
  const topTicketPlatform = platformStats.reduce((max, p) => p.avgTicket > max.avgTicket ? p : max, platformStats[0] || { platform: '', conversions: 0, revenue: 0, conversionRate: 0, avgTicket: 0 });

  if (metrics.length === 0) {
    return (
      <div className="space-y-6">
        {/* Empty State */}
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="mb-6 p-8 bg-primary/10 rounded-full">
            <TrendingUp className="h-12 w-12 text-primary" />
          </div>
          
          <h3 className="text-xl font-semibold mb-2">
            {translateInterfaceText("Nenhum dado disponível ainda")}</h3>
          
          <p className="text-muted-foreground mb-6 max-w-md">
            {translateInterfaceText("Integre suas plataformas para acompanhar métricas de vendas e conversões.")}</p>
        </div>
      </div>
    );
  }

  // Compare Mode Rendering
  if (compareMode && selectedComparePlatforms.length >= 2) {
    // Consolidate data by platform
    const consolidatedData = selectedComparePlatforms.map(platformId => {
      const metrics = getMetricsForPlatform(platformId);
      const avgTicket = metrics.conversions > 0 ? metrics.revenue / metrics.conversions : 0;
      
      return {
        platformId,
        platformName: getPlatformName(platformId),
        conversions: metrics.conversions,
        revenue: metrics.revenue,
        conversionRate: metrics.conversionRate,
        avgTicket,
        videos: metrics.metrics
      };
    });
    
    // Sort consolidated data
    const sortedConsolidatedData = [...consolidatedData].sort((a, b) => {
      if (!sortField) {
        // Default sort by conversions descending
        return b.conversions - a.conversions;
      }
      
      let aValue: number;
      let bValue: number;
      
      if (sortField === 'conversions') {
        aValue = a.conversions;
        bValue = b.conversions;
      } else if (sortField === 'revenue') {
        aValue = a.revenue;
        bValue = b.revenue;
      } else if (sortField === 'conversionRate') {
        aValue = a.conversionRate;
        bValue = b.conversionRate;
      } else {
        return 0;
      }
      
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    // Prepare data for PerformanceHighlights
    const platformsDataForHighlights = consolidatedData.map(p => ({
      id: p.platformId,
      name: p.platformName,
      data: {
        conversions: p.conversions,
        revenue: p.revenue,
        conversionRate: p.conversionRate
      }
    }));
    
    return (
      <div className="space-y-6">
        {/* Compare Mode Header with integrated platform selection */}
        <div className="flex flex-col gap-4">
          {/* Top row: Period Filter + Action Buttons */}
          <div className="flex items-center justify-between gap-3">
            {/* Period Filter */}
            <PeriodPicker
              value={customDateRange}
              onChange={setCustomDateRange}
              placeholder={translateInterfaceText("Selecione")}
              className="w-[160px] sm:w-[200px] lg:w-[240px]"
            />

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCompareMode(false)}
                className="gap-2 bg-white text-[#475467] border-[#E4E7EC] hover:bg-[#F9FAFB] hover:text-[#475467]"
              >
                {translateInterfaceText("← Voltar para Métricas")}</Button>
              <Button
                variant="default"
                size="sm"
                onClick={exportToPDF}
                className="gap-2 bg-[#2563EB] hover:bg-[#2563EB]/90 rounded-lg"
              >
                <Download className="h-4 w-4" />
                {translateInterfaceText("Exportar")}</Button>
            </div>
          </div>

          {/* Platform Selection Header - Integrated */}
          <PlatformComparisonHeader
            availablePlatforms={activePlatforms}
            selectedPlatforms={selectedComparePlatforms}
            onPlatformsChange={setSelectedComparePlatforms}
            maxPlatforms={4}
          />
        </div>

        <div ref={comparisonRef} className="space-y-6">
          {/* Performance Highlights */}
          <PerformanceHighlights platforms={platformsDataForHighlights} />

          {/* Separador Premium */}
          <div className="h-px bg-[#F2F4F7]" />

          {/* Consolidated Comparison Table */}
          <div className="bg-white border border-[#E7E9EF] rounded-[14px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAFA] border-b border-[#E7E9EF] hover:bg-[#FAFAFA]">
                  <TableHead className="font-semibold text-sm text-[#374151] h-12">
                    {translateInterfaceText("Plataforma")}</TableHead>
                  <TableHead 
                    className="text-right font-semibold text-sm text-[#374151] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('conversions')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {translateInterfaceText("Conversões")}<SortIcon field="conversions" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right font-semibold text-sm text-[#374151] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('conversionRate')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {translateInterfaceText("Taxa Média")}<SortIcon field="conversionRate" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold text-sm text-[#374151]">
                    {translateInterfaceText("Ticket Médio")}</TableHead>
                  <TableHead 
                    className="text-right font-semibold text-sm text-[#374151] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('revenue')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {translateInterfaceText("Receita Total")}<SortIcon field="revenue" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold text-sm text-[#374151]">
                    {translateInterfaceText("Ações")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Platform Rows */}
                {sortedConsolidatedData.map((platform) => (
                  <TableRow 
                    key={platform.platformId}
                    className="h-12 border-b border-[#E5E7EB] hover:bg-[#F8F9FA] transition-colors"
                  >
                    <TableCell className="py-3">
                      <img 
                        src={getPlatformLogo(platform.platformId)} 
                        alt={platform.platformName}
                        className={`object-contain opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-200 ${
                          platform.platformId === 'braip' ? 'max-h-[28px] max-w-[70px]' : 'max-h-[32px] max-w-[80px]'
                        }`}
                      />
                    </TableCell>
                    <TableCell className="text-right text-[15px] text-[#111827]">
                      {platform.conversions}
                    </TableCell>
                    <TableCell className="text-right text-[15px] text-[#111827]">
                      {platform.conversionRate.toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right text-[15px] text-[#111827]">
                      R$ {platform.avgTicket.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right text-[15px] text-[#111827]">
                      R$ {platform.revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPlatformForModal({
                            platformId: platform.platformId,
                            platformName: platform.platformName,
                            videos: platform.videos
                          });
                          setShowDetailsModal(true);
                        }}
                        className="text-[#2563EB] hover:text-[#2563EB] hover:bg-[#F0F5FF] text-sm font-medium"
                      >
                        {translateInterfaceText("Ver detalhes")}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        
        {/* Platform Details Modal */}
        {selectedPlatformForModal && (
          <PlatformDetailsModal
            open={showDetailsModal}
            onOpenChange={setShowDetailsModal}
            platformId={selectedPlatformForModal.platformId}
            platformName={selectedPlatformForModal.platformName}
            videos={selectedPlatformForModal.videos}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
        <KPICard
          title={translateInterfaceText("Conversões Totais")}
          value={totalConversions.toLocaleString(getInterfaceLanguage())}
          tooltip={translateInterfaceText("Número total de vendas confirmadas pelas plataformas de pagamento")}
          platformLogo={topConversionsPlatform.platform ? getPlatformLogo(topConversionsPlatform.platform) : undefined}
          platformName={topConversionsPlatform.platform ? getPlatformName(topConversionsPlatform.platform) : undefined}
        />
        <KPICard
          title={translateInterfaceText("Taxa Média")}
          value={`${weightedConversionRate.toFixed(2)}%`}
          tooltip={translateInterfaceText("Taxa média de conversão calculada entre todas as plataformas")}
          platformLogo={topRatePlatform.platform ? getPlatformLogo(topRatePlatform.platform) : undefined}
          platformName={topRatePlatform.platform ? getPlatformName(topRatePlatform.platform) : undefined}
        />
        <KPICard
          title={translateInterfaceText("Ticket Médio")}
          value={`R$ ${averageTicket.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}`}
          tooltip={translateInterfaceText("Valor médio por venda realizada no período")}
          platformLogo={topTicketPlatform.platform ? getPlatformLogo(topTicketPlatform.platform) : undefined}
          platformName={topTicketPlatform.platform ? getPlatformName(topTicketPlatform.platform) : undefined}
        />
        <KPICard
          title={translateInterfaceText("Receita Total")}
          value={`R$ ${totalRevenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}`}
          tooltip={translateInterfaceText("Valor total de vendas confirmadas no período")}
          platformLogo={topRevenuePlatform.platform ? getPlatformLogo(topRevenuePlatform.platform) : undefined}
          platformName={topRevenuePlatform.platform ? getPlatformName(topRevenuePlatform.platform) : undefined}
        />
      </div>
      
      {/* Modals */}
      <ConversionModal
        open={openModal === 'conversions'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={totalConversions}
        revenue={totalRevenue}
        conversionRate={weightedConversionRate}
        averageTicket={averageTicket}
        platformLogo={topConversionsPlatform.platform ? getPlatformLogo(topConversionsPlatform.platform) : undefined}
        platformName={topConversionsPlatform.platform ? getPlatformName(topConversionsPlatform.platform) : undefined}
      />
      <ConversionRateModal
        open={openModal === 'rate'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={totalConversions}
        revenue={totalRevenue}
        conversionRate={weightedConversionRate}
        averageTicket={averageTicket}
        platformLogo={topRatePlatform.platform ? getPlatformLogo(topRatePlatform.platform) : undefined}
        platformName={topRatePlatform.platform ? getPlatformName(topRatePlatform.platform) : undefined}
      />
      <AverageTicketModal
        open={openModal === 'ticket'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={totalConversions}
        revenue={totalRevenue}
        conversionRate={weightedConversionRate}
        averageTicket={averageTicket}
        platformLogo={topTicketPlatform.platform ? getPlatformLogo(topTicketPlatform.platform) : undefined}
        platformName={topTicketPlatform.platform ? getPlatformName(topTicketPlatform.platform) : undefined}
      />
      <RevenueModal
        open={openModal === 'revenue'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={totalConversions}
        revenue={totalRevenue}
        conversionRate={weightedConversionRate}
        averageTicket={averageTicket}
        platformLogo={topRevenuePlatform.platform ? getPlatformLogo(topRevenuePlatform.platform) : undefined}
        platformName={topRevenuePlatform.platform ? getPlatformName(topRevenuePlatform.platform) : undefined}
      />

      {/* Separador Premium */}
      <div className="h-px bg-[#F2F4F7] my-8" />

      {/* Premium Table Card */}
      <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
        <Table>
          {dateRange?.from && dateRange?.to ? (
            <>
              <TableHeader>
                <TableRow className="bg-[#FAFAFA] border-b border-border hover:bg-[#FAFAFA]">
                  <TableHead className="font-semibold text-[#4B5563] h-12">{translateInterfaceText("Plataforma")}</TableHead>
                  <TableHead className="font-semibold text-[#4B5563]">{translateInterfaceText("Vídeo")}</TableHead>
                  <TableHead
                    className="text-right font-semibold text-[#4B5563] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('conversions')}
                  >
                    <div className="flex items-center justify-end">
                      {translateInterfaceText("Conversões")}<SortIcon field="conversions" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-right font-semibold text-[#4B5563] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('revenue')}
                  >
                    <div className="flex items-center justify-end">
                      {translateInterfaceText("Receita")}<SortIcon field="revenue" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold text-[#4B5563]">
                    {translateInterfaceText("Ticket Médio")}</TableHead>
                  <TableHead
                    className="text-right font-semibold text-[#4B5563] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('conversionRate')}
                  >
                    <div className="flex items-center justify-end">
                      {translateInterfaceText("Taxa de conversão")}<SortIcon field="conversionRate" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold text-sm text-[#374151]">{translateInterfaceText("Data")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!metricsByDay || metricsByDay.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-[#9CA3AF] text-sm">
                      {translateInterfaceText("Nenhum dado encontrado para o período selecionado.")}</TableCell>
                  </TableRow>
                ) : (
                  <>
                    {metricsByDay.map((row, index) => (
                      <TableRow key={index} className="h-12 border-b border-[#E5E7EB]">
                        <TableCell>
                          {row.platformLogo ? (
                            <img
                              src={row.platformLogo}
                              alt={row.platformName || ''}
                              className="max-h-[28px] max-w-[70px] object-contain opacity-90"
                            />
                          ) : (
                            <span className="text-[15px] text-[#374151]">{row.platformName || '—'}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-[15px] text-[#374151] max-w-[180px] truncate">
                          {row.videoName || '—'}
                        </TableCell>
                        <TableCell className="text-right text-[15px]">
                          {row.conversions}
                        </TableCell>
                        <TableCell className="text-right text-[15px]">
                          R$ {Number(row.revenue).toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-[15px]">
                          R$ {Number(row.averageTicket).toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-[15px]">
                          {Number(row.conversionRate).toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right text-[15px] text-[#374151] font-medium whitespace-nowrap">
                          {format(new Date(row.date + 'T00:00:00'), "PPP", { locale: getInterfaceDateLocale() })}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow className="bg-[#F4F8FF] border-t-[1.5px] border-[#BFD8FF] hover:bg-[#F4F8FF] h-12">
                      <TableCell className="font-semibold text-[#374151]">
                        <div className="flex items-center gap-2">
                          <Layers className="h-5 w-5 text-[#2563EB]" />
                          <span>{translateInterfaceText("Total do período")}</span>
                        </div>
                      </TableCell>
                      <TableCell />
                      <TableCell className="text-right font-semibold text-foreground text-[15px]">
                        {metricsByDay.reduce((s, r) => s + r.conversions, 0)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-foreground text-[15px]">
                        R$ {metricsByDay.reduce((s, r) => s + Number(r.revenue), 0).toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-foreground text-[15px]">
                        —
                      </TableCell>
                      <TableCell className="text-right font-semibold text-foreground text-[15px]">
                        {(metricsByDay.reduce((s, r) => s + Number(r.conversionRate), 0) / metricsByDay.length).toFixed(2)}%
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </>
          ) : (
            <>
              <TableHeader>
                <TableRow className="bg-[#FAFAFA] border-b border-border hover:bg-[#FAFAFA]">
                  <TableHead className="font-semibold text-[#4B5563] h-12">
                    <div className="inline-flex items-center gap-2">
                      <span className="font-semibold text-sm text-[#374151]">{translateInterfaceText("Plataforma")}</span>
                      <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                        <SelectTrigger className="border-none bg-transparent p-0 h-auto w-auto gap-1 hover:text-[#2563EB] transition-colors focus:ring-0 focus:ring-offset-0 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:text-[#6B7280]">
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="all">{translateInterfaceText("Todas as Plataformas")}</SelectItem>
                          {activePlatforms.map((platform) => (
                            <SelectItem key={platform} value={platform}>
                              {getPlatformName(platform)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-[#4B5563]">{translateInterfaceText("Vídeo")}</TableHead>
                  <TableHead
                    className="text-right font-semibold text-[#4B5563] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('conversions')}
                  >
                    <div className="flex items-center justify-end">
                      {translateInterfaceText("Conversões")}<SortIcon field="conversions" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-right font-semibold text-[#4B5563] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('revenue')}
                  >
                    <div className="flex items-center justify-end">
                      {translateInterfaceText("Receita")}<SortIcon field="revenue" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-right font-semibold text-[#4B5563] cursor-pointer hover:text-[#2563EB] transition-colors"
                    onClick={() => handleSort('conversionRate')}
                  >
                    <div className="flex items-center justify-end">
                      {translateInterfaceText("Taxa de conversão")}<SortIcon field="conversionRate" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMetrics.map((metric, index) => (
                  <TableRow key={index} className="h-12 border-b border-[#E5E7EB]">
                    <TableCell className="pr-6">
                      <div className="flex items-center">
                        <img
                          src={getPlatformLogo(metric.platform)}
                          alt={getPlatformName(metric.platform)}
                          className={`max-w-[80px] rounded-md opacity-90 object-contain transition-all duration-200 hover:opacity-100 hover:scale-105 ${metric.platform === 'braip' ? 'max-h-[32px]' : 'max-h-[40px]'}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-[15px] pl-6">{metric.video}</TableCell>
                    <TableCell className="text-right text-[15px]">{metric.conversions}</TableCell>
                    <TableCell className="text-right text-[15px]">
                      R$ {metric.revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right text-[15px]">
                      {metric.conversionRate.toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-[#F4F8FF] border-t-[1.5px] border-[#BFD8FF] hover:bg-[#F4F8FF] h-12">
                  <TableCell className="font-semibold text-[#374151]">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-[#2563EB]" />
                      <span>{translateInterfaceText("Todas as Plataformas")}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold text-foreground text-[15px]">{translateInterfaceText("Total geral")}</TableCell>
                  <TableCell className="text-right font-semibold text-foreground text-[15px]">{totalConversions}</TableCell>
                  <TableCell className="text-right font-semibold text-foreground text-[15px]">
                    R$ {totalRevenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-foreground text-[15px]">
                    {weightedConversionRate.toFixed(2)}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </>
          )}
        </Table>
      </div>
    </div>
  );
}
